import { convertToJSRecursive } from '../../utils/redux-store/immutableSerialize';
import {
  DASHBOARD_REPORT_LOCATIONS,
  type EntityValue,
  type GeneratedReportsState,
  type Report,
  type ReportCell,
  type ReportRow,
} from './generatedReportsTypes';

/** Reports whose generated results may be restored from the local cache. */
export const PERSISTED_REPORT_NAMES: readonly string[] = ['spending-dashboard', 'net-worth-page'];

const persistedReportNames = new Set(PERSISTED_REPORT_NAMES);

// Saved/detail report instances stay transient; only the dashboard's own slots are cached.
const dashboardReportLocations = new Set<string>(Object.values(DASHBOARD_REPORT_LOCATIONS));

const isPersistedReport = (entity: EntityValue | undefined): boolean => {
  const report = entity?.report;
  if (!entity || !report) return false;
  if (dashboardReportLocations.has(entity.location)) return true;
  // Only the tab's own instance: other features (e.g. Wellness) generate derived reports under
  // their own locations from the same named configuration.
  return persistedReportNames.has(report.configuration.name) && entity.location === report.configuration.id;
};

/** Cached dashboard charts need totals, not transaction drilldown items. */
function stripDashboardCellItems(report: Report): Report {
  let rows: ReportRow[] | undefined;
  report.rows.forEach((row, rowIndex) => {
    let cells: ReportCell[] | undefined;
    row.cellData.forEach((cell, cellIndex) => {
      if (!('items' in cell)) return;

      cells ??= [...row.cellData];
      const cachedCell = { ...cell };
      if (cell.items) cachedCell.itemCount = cell.items.length;
      delete cachedCell.items;
      cells[cellIndex] = cachedCell;
    });

    if (cells) {
      rows ??= [...report.rows];
      rows[rowIndex] = { ...row, cellData: cells };
    }
  });

  return rows ? { ...report, rows } : report;
}

/** Skip serialization unless an allowed report completed generation. */
export function shouldPersistGeneratedReports(
  lastState: GeneratedReportsState | undefined,
  nextState: GeneratedReportsState | undefined,
): boolean {
  if (!nextState || lastState?.entities === nextState.entities) return false;

  for (const id of nextState.ids) {
    const nextEntity = nextState.entities[id];
    if (isPersistedReport(nextEntity) && nextEntity?.generation !== lastState?.entities[id]?.generation) {
      return true;
    }
  }
  // don't remove existing entries unless there is a newly-generated entry to push

  return false;
}

export function stateToCacheGeneratedReports(state: GeneratedReportsState): object {
  const entities: Record<string, EntityValue> = {};
  const ids: string[] = [];

  for (const id of state.ids) {
    const entity = state.entities[id];
    if (!entity?.report || !isPersistedReport(entity)) continue;

    ids.push(id);
    entities[id] = {
      location: entity.location,
      report: stripDashboardCellItems(entity.report),
      isGenerating: false,
      generation: entity.generation,
    };
  }

  // This counter belongs to the current session. Restored results must regenerate
  // because other data may have changed after the last cache write.
  return convertToJSRecursive({
    ids,
    entities,
    activeViewingConfigId: null,
    dataVersion: 0,
  });
}
